import { Box, CircularProgress, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect } from 'react';
import Button from '@/common/presentation/components/inputs/Button';
import CustomerModalFormFields from './CustomerModalFormFields';
import { CustomerModalValidationSchema } from '@/customers/presentation/CustomerValidationSchema';
import { CustomerFormData } from '@/customers/application/types/CustomerTypes';
import { FormikSubmitFormButton } from '@/common/presentation/components/forms/FormikSubmitFormButton/FormikSubmitFormButton';
import { useCustomerForm } from '@/customers/presentation/hooks/useCustomerForm';
import { useOptionalFormikContext } from '@/common/presentation/hooks/useOptionalFormikContext';
import tid from '@/common/infrastructure/config/tid';
import { BORDER_COLORS } from '@/common/presentation/constants/colors';

export default function CustomerModalForm({
  initialValues,
  isNew,
  formikStatus,
  onClose,
  loading,
  open,
  onSuccess
}: Readonly<{
  initialValues: CustomerFormData;
  isNew: boolean;
  formikStatus: { loading: boolean };
  onClose: () => void;
  loading: boolean;
  open: boolean;
  onSuccess?: (customer: { id: number; companyName: string }) => void;
}>) {
  const title = isNew ? 'Create Customer' : 'Update Customer';

  // Compute customerId only after initialValues exists (for edit mode)
  const customerId = isNew ? 0 : initialValues?.id;
  const isValidCustomerId = customerId && typeof customerId === 'number' && customerId > 0;
  const hasInvalidCustomerId = open && !isNew && initialValues && !isValidCustomerId;

  // All hooks must be called before any early returns (Rules of Hooks)
  // Only call useCustomerForm when we have valid data (isNew is true or validated customerId > 0)
  const { handleSubmit, updateLoading } = useCustomerForm(isNew, isValidCustomerId ? customerId : 0, onClose, onSuccess);

  // Optional parent formik
  const parentFormik = useOptionalFormikContext<CustomerFormData>();

  // Log error in useEffect (outside render) when invalid customerId is detected after loading
  useEffect(() => {
    if (hasInvalidCustomerId) {
      console.error('Customer ID is required for editing. Cannot update customer without a valid ID.', {
        isNew,
        customerId,
        initialValues,
        open
      });
    }
  }, [hasInvalidCustomerId, isNew, customerId, initialValues, open]);

  // Handle loading state: if editing and initialValues is not yet loaded, show loading placeholder
  if (!isNew && !initialValues && open) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Loading customer data...
        </Typography>
      </Box>
    );
  }

  // Return early with error message if customerId is invalid after initialValues is loaded
  if (hasInvalidCustomerId) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
        <Typography variant="body1" color="error" sx={{ mb: 1 }}>
          Invalid Customer ID
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Unable to load customer data. Please try again or contact support.
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      enableReinitialize
      validateOnChange
      initialValues={initialValues || parentFormik?.initialValues || ({} as CustomerFormData)}
      validationSchema={CustomerModalValidationSchema}
      onSubmit={handleSubmit}
      initialStatus={formikStatus}
    >
      {({ isSubmitting }) => (
        <Form>
          {/* Customer Form Fields */}
          <CustomerModalFormFields isNew={isNew} open={open} />

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${BORDER_COLORS.LIGHT}`
            }}
          >
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                textTransform: 'uppercase',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '24px',
                color: 'primary.main'
              }}
              disabled={isSubmitting || updateLoading}
              {...tid('customers:management:cancel-button')}
            >
              Cancel
            </Button>

            <FormikSubmitFormButton
              variant="contained"
              sx={{
                paddingX: 2,
                textTransform: 'uppercase',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '24px'
              }}
              type="submit"
              disabled={isSubmitting || updateLoading || loading}
              isHotkeyEnabled={open}
              testId="customers:management:save-button"
            >
              {isSubmitting || updateLoading ? 'Saving...' : title}
            </FormikSubmitFormButton>
          </Box>
        </Form>
      )}
    </Formik>
  );
}
